import Block from '@/components/Block';
import { UpdateQuery } from '@/components/hooks/useQueryString';
import { POS_Header_Height } from '@/components/LayoutPOS/Header';
import ModalSelect from '@/components/ModalSelect';
import PosButton from '@/components/PosButton';
import { BaseInfoContext } from '@/context/BaseInfoContext';
import {
  Appointment,
  AppointmentChannelDesc,
  AppointmentEndpoint,
  AppointmentStatus,
  calcAppointmentDuration,
  getAppointmentStatusTag,
} from '@/models/appointment';
import { DateTimeDisplayFormat, Page } from '@/models/common';
import { useBackToIndexOnPos } from '@/utils/app';
import { IS_POS } from '@/utils/env';
import { playSound } from '@/utils/sound';
import { FileDoneOutlined, SearchOutlined } from '@ant-design/icons';
import { Form, Grid, List, Select, Space, Table, Tag } from 'antd';
import { ColumnProps } from 'antd/es/table';
import dayjs from 'dayjs';
import { Key, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useRequest } from '../../api/hooks';

type Props = {
  /**
   * mini 用于 POS 用户卡: List
   * 默认: Table
   */
  miniMode?: boolean;
  defaultQuery: Partial<{
    page: number;
    pageSize: number;
    sort: string;
    status: string;
    userId: string;
  }>;
  updateQuery: UpdateQuery<Props['defaultQuery']>;
};

const AppointmentList = ({ miniMode = false, defaultQuery, updateQuery: updateQueryOrigin }: Props) => {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { xs } = Grid.useBreakpoint();
  const [, , helpers] = useContext(BaseInfoContext);
  const [currentQuery, setCurrentQuery] = useState(defaultQuery);

  const updateQuery: typeof updateQueryOrigin = params => {
    if (IS_POS) {
      playSound('click');
    }
    const newParams = Object.assign({}, currentQuery, params);
    setCurrentQuery(newParams);
    updateQueryOrigin(newParams, { preventScrollReset: true });
    form.setFieldsValue(newParams);
  };

  const { payload, send, loaded, loading, error } = useRequest<{
    list: Appointment[];
    page: Page;
  }>(
    {
      method: 'get',
      url: `${AppointmentEndpoint}`,
    },
    {
      manual: true,
    }
  );

  useEffect(() => {
    send({
      params: {
        ...currentQuery,
      },
    }).then();
  }, [currentQuery]);

  useBackToIndexOnPos();

  const toDetail = (appointmentId: Key) => {
    navigate(
      {
        // pathname: generatePath(path, { orderSn: record.orderSn }),
        ...location,
        pathname: `${appointmentId}`,
      },
      { replace: true, preventScrollReset: true }
    );
  };

  const showDetail = (appointmentId: Key) => {
    helpers.showAppointmentModal({
      appointmentId: `${appointmentId}`,
      onClose: v => {
        if (v) {
          send({
            params: {
              ...currentQuery,
            },
          }).then();
        }
        if (!miniMode) {
          navigate(
            {
              ...location,
              pathname: location.pathname.replace(/\/\d+[^/]+$/, ''),
            },
            { replace: true, preventScrollReset: true }
          );
        }
      },
    });
  };

  useEffect(() => {
    if (miniMode) return;
    if (appointmentId) {
      showDetail(appointmentId);
    } else {
      helpers.closeAppointmentModal();
    }
  }, [appointmentId]);

  const columns = useMemo<ColumnProps<Appointment>[]>(() => {
    const align = 'center' as 'center';
    return [
      {
        title: '预约时间',
        fixed: 'left',
        align: 'center' as 'center',
        dataIndex: 'startTime',
        width: xs ? 85 : 120,
        render: (_, record) => {
          return dayjs(record.startTime).format(DateTimeDisplayFormat);
        },
      },
      {
        title: '人数',
        align,
        width: 50,
        dataIndex: 'count',
      },
      {
        title: '状态',
        align,
        width: 100,
        // responsive: ['sm'],
        dataIndex: 'status',
        render: (_, record) => (
          <span
            onClick={() => {
              if (xs) {
                toDetail(record.id);
              }
            }}>
            {getAppointmentStatusTag(record.status)}
          </span>
        ),
      },
      {
        title: '时长',
        align,
        width: 50,
        key: 'duration',
        render: (_, record) => {
          return <>{calcAppointmentDuration(record)}</>;
        },
      },
      {
        title: '渠道',
        align,
        width: 100,
        // responsive: ['sm'],
        dataIndex: 'status',
        render: (_, record) => <div>{AppointmentChannelDesc[record.channel]}</div>,
      },
      {
        title: '备注',
        dataIndex: 'memo',
        width: 120,
        align,
      },
      {
        title: '操作',
        dataIndex: 'id',
        width: 130,
        align,
        render: (_, record) => {
          return (
            <Space size={5} direction={'vertical'}>
              <PosButton
                onClick={() => {
                  toDetail(record.id);
                }}>
                <FileDoneOutlined /> 预约详情
              </PosButton>
            </Space>
          );
        },
      },
    ];
  }, [payload, xs]);

  return (
    <>
      <Block size="small" style={{ display: miniMode ? 'none' : undefined }}>
        <Form
          form={form}
          initialValues={currentQuery}
          layout="inline"
          onFinish={query => {
            updateQuery({
              ...query,
              page: 1,
            });
          }}>
          <Form.Item name="sort">
            <ModalSelect
              placeholder="请选择排序方式"
              style={{ width: 170 }}
              onChange={() => {
                form.submit();
              }}>
              <Select.Option value="start_time">预约时间</Select.Option>
              <Select.Option value="add_time">创建时间</Select.Option>
            </ModalSelect>
          </Form.Item>

          <Form.Item name="status">
            <ModalSelect
              placeholder={'预约单状态'}
              style={{ width: 150 }}
              onChange={() => {
                form.submit();
              }}>
              <Select.Option value={''}>全部</Select.Option>
              <Select.Option value={`${AppointmentStatus.CONFIRMED}`}>预约成功</Select.Option>
              <Select.Option value={`${AppointmentStatus.CHECKIN}`}>已到店</Select.Option>
              <Select.Option value={`${AppointmentStatus.COMPLETE}`}>已完成</Select.Option>
              <Select.Option value={`${AppointmentStatus.CANCELED}`}>已取消</Select.Option>
            </ModalSelect>
          </Form.Item>

          <Form.Item>
            <PosButton htmlType="submit" icon={<SearchOutlined />} loading={loading}>
              查询
            </PosButton>
          </Form.Item>
        </Form>
      </Block>
      {miniMode ? (
        <>
          <List
            loading={loading}
            itemLayout="horizontal"
            size={'small'}
            loadMore={
              <div className={'align-flex-horizontal-center'} style={{ paddingTop: 15 }}>
                <PosButton
                  disabled={payload?.page.current === 1}
                  loading={loading}
                  style={{ marginRight: 10 }}
                  onClick={() => {
                    updateQuery({ page: payload?.page.current - 1 });
                  }}>
                  上一页
                </PosButton>
                <PosButton
                  disabled={payload?.page.current === payload?.page.pages}
                  loading={loading}
                  style={{ marginLeft: 10 }}
                  onClick={() => {
                    updateQuery({ page: payload?.page.current + 1 });
                  }}>
                  下一页
                </PosButton>
              </div>
            }
            dataSource={payload?.list}
            renderItem={record => (
              <List.Item
                actions={[
                  <PosButton
                    key={'detail'}
                    onClick={() => {
                      showDetail(record.id);
                    }}>
                    详情
                  </PosButton>,
                ]}>
                <List.Item.Meta
                  title={
                    <>
                      <span style={{ display: 'inline-block' }}>预约时间: {record.startTime}</span>{' '}
                    </>
                  }
                  description={
                    <Space direction={'vertical'} size={5}>
                      <Space size={10}>
                        <div>{getAppointmentStatusTag(record.status)}</div>
                        <div>
                          <Tag>{record.count} 人</Tag>
                        </div>
                      </Space>
                      <div>备注: {record.memo || '-'}</div>
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        </>
      ) : (
        <Block ghostFull>
          <Table<Appointment>
            size="small"
            bordered
            sticky={{ offsetHeader: IS_POS ? POS_Header_Height : 0 }}
            loading={loading}
            scroll={{ x: 'max-content' }}
            dataSource={payload?.list}
            columns={columns}
            rowKey={`id`}
            pagination={{
              ...payload?.page,
              size: 'default',
              showLessItems: xs,
              showSizeChanger: false,
              onChange: (page, pageSize) => {
                updateQuery({ page, pageSize });
              },
              style: { marginBottom: 0 },
            }}
          />
        </Block>
      )}
    </>
  );
};

export default AppointmentList;
